import { Route } from 'vue-router';
import { getCache } from '../storage/storageFactory';
import { BasePaymentStorage } from '../storage/types/BaseStorage';
import { AbstractStorage } from '../storage/abstractStorage';
import { LocalStorage } from '../storage/localStorage';
import { BaseMoney } from '../../store/price/types';

const cacheEngine = 'localStorage';

export const enum PaymentStorageModes {
  RECHARGE = 'recharge',
  CHECKOUT = 'checkout',
  SETUP_SPM = 'setup'
}

export class PaymentStorageService extends LocalStorage<BasePaymentStorage> {
  protected cache: AbstractStorage<BasePaymentStorage> = null;

  static readonly PAYMENT_STORAGE_KEY = 'payment';

  static readonly CACHE_STORAGE_TYPE = 'localStorage';

  static readonly PAYMENT_ROUTE_PARAMETER = 'pid';

  static isPaymentRoute(route: Route): boolean {
    const parameter = PaymentStorageService.PAYMENT_ROUTE_PARAMETER;

    if (!route) return false;

    return parameter in route.query;
  }

  static create(payment: BasePaymentStorage): PaymentStorageService {
    const cache = getCache(PaymentStorageService.CACHE_STORAGE_TYPE);

    if (!cache.isAvailable()) {
      throw new Error('storage is not available');
    }

    if (!cache.set(PaymentStorageService.PAYMENT_STORAGE_KEY, payment, -1)) {
      throw new Error('could not create payment');
    }

    return new this();
  }

  static get(): PaymentStorageService {
    const cache = getCache(PaymentStorageService.CACHE_STORAGE_TYPE);

    if (!cache.isAvailable()) {
      throw new Error('storage is not available');
    }

    if (!cache.get(PaymentStorageService.PAYMENT_STORAGE_KEY)) {
      return null;
    }

    return new this();
  }

  /**
     * returns the current payment from the storage, this means its **NOT** side-effect free
     * and this value is MUTABLE
     */
  getPayment(): BasePaymentStorage {
    return this.get(PaymentStorageService.PAYMENT_STORAGE_KEY);
  }

  isPaymentTracked(): boolean {
    const payment = this.getPayment();

    return payment ? payment.tracked : false;
  }

  setPaymentTracked(value: boolean): void {
    this.set<BasePaymentStorage>(PaymentStorageService.PAYMENT_STORAGE_KEY, {
      ...this.getPayment(),
      tracked: value,
    }, -1);
  }

  setPrice(price: BaseMoney): void {
    this.set<BasePaymentStorage>(PaymentStorageService.PAYMENT_STORAGE_KEY, {
      ...this.getPayment(),
      amount: price.amount,
      currency: price.currency,
    }, -1);
  }

  equalId(id: string): boolean {
    const payment = this.getPayment();

    return payment ? payment.id === id : false;
  }

  equalMode(mode: string): boolean {
    const payment = this.getPayment();

    return payment ? payment.mode === mode : false;
  }
}

/**
 * creates a payment in the cache storage
 * @deprecated prefer using the PaymentStorageService::create
 * @param payment
 */
export const createPaymentInCache = (payment: BasePaymentStorage) => {
  const cache = getCache(cacheEngine);

  if (!cache.isAvailable()) {
    throw new Error('storage is not available!');
  }
  cache.set<BasePaymentStorage>('payment', payment, -1);
};
